import React, { createContext, useContext } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import Sidebar from './components/Sidebar';
import Header from './components/Header';
import Dashboard from './pages/Dashboard';
import Mahsulotlar from './pages/Mahsulotlar';
import Xaridlar from './pages/Xaridlar';
import Kassa from './pages/Kassa';
import Chiqimlar from './pages/Chiqimlar';
import Taminlovchilar from './pages/Taminlovchilar';
import Kategoriyalar from './pages/Kategoriyalar';
import Birliklar from './pages/Birliklar';
import Xodimlar from './pages/Xodimlar';
import Hisobotlar from './pages/Hisobotlar';
import Login from './pages/Login';
import Register from './pages/Register';
import { ToastContainer } from 'react-toastify';
import Mijozlar from './pages/Mijozlar';
import StoreHouse from './pages/StoreHouse';
import Mydebt from './pages/Mydebt';
import Store from './pages/Store';
import Customer3 from './pages/Customer3Page';
import Kreditlar from './pages/Kreditlar';
import AdminLogin from './pages/AdminLogin';
import AdminDashboard from './pages/AdminDashboard';
import Ruxsatlar from './pages/Ruxsatlar';
import PermissionProtectedRoute from './components/PermissionProtectedRoute';
import MoveStore from './pages/MoveStore';
import Refunds from './pages/Refund';
import Inventory from './pages/Inventory';
import Booking from './pages/Booking';

import 'react-toastify/dist/ReactToastify.css';
import './App.css';

import { ExchangeRateProvider } from './ExchangeRateContext';
import InactiveAccount from './components/InactiveAccount';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { PermissionProvider } from './contexts/PermissionContext';

// Sidebar context to share collapsed state
const SidebarContext = createContext();
export const useSidebar = () => useContext(SidebarContext);

// Protected Route component
const ProtectedRoute = ({ children, allowedRoles = [] }) => {
  const { isAuthenticated, loading, user } = useAuth();
  const location = useLocation();

  if (loading) {
    return <div className="d-flex justify-content-center align-items-center min-vh-100">
      <div className="spinner-border" role="status">
        <span className="visually-hidden">Loading...</span>
      </div>
    </div>;
  }

  // Check for admin routes specifically
  if (location.pathname.startsWith('/admin')) {
    const adminToken = localStorage.getItem('adminToken');
    if (!adminToken) {
      return <Navigate to="/admin-login" state={{ from: location }} replace />;
    }
    return children;
  }

  if (!isAuthenticated()) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (allowedRoles.length > 0 && !allowedRoles.includes(user.role)) {
    return <Navigate to="/" state={{ from: location }} replace />;
  }

  return children;
};

// Admin Protected Route
const AdminRoute = ({ children }) => {
  return <ProtectedRoute allowedRoles={['admin']}>{children}</ProtectedRoute>;
};

function AppRoutes() {
  const location = useLocation();
  const { user, ownerData, inactive } = useAuth();
  const [collapsed, setCollapsed] = React.useState(location.pathname === '/kassa');

  // Update collapsed state when route changes
  React.useEffect(() => {
    setCollapsed(location.pathname === '/kassa');
  }, [location.pathname]);

  // If account is inactive (owner or staff), show special screen and hide app chrome
  if (inactive || (user?.role === 'owner' && ownerData && ownerData.isactive === false)) {
    return <InactiveAccount />;
  }

  return (
    <SidebarContext.Provider value={{ collapsed, setCollapsed }}>
      <div className="container-fluid min-vh-100 p-0 app-layout">
        {location.pathname !== '/kassa' && <Header />}
        <div className="app-body">
          <Sidebar />
          <div className={`main-content${collapsed ? ' collapsed' : ''}`}>
            <Routes>
              <Route path="/" element={
                user?.role === 'staff' ? (
                  <Navigate to="/kassa" replace />
                ) : (
                  <PermissionProtectedRoute pagePath="/">
                    <Dashboard />
                  </PermissionProtectedRoute>
                )
              } />
              <Route
                path="/mahsulotlar"
                element={
                  <PermissionProtectedRoute pagePath="/mahsulotlar">
                    <Mahsulotlar />
                  </PermissionProtectedRoute>
                }
              />
              <Route
                path="/xaridlar"
                element={
                  <PermissionProtectedRoute pagePath="/xaridlar">
                    <Xaridlar />
                  </PermissionProtectedRoute>
                }
              />
              <Route
                path="/kassa"
                element={
                  <PermissionProtectedRoute pagePath="/kassa">
                    <Kassa />
                  </PermissionProtectedRoute>
                }
              />
              <Route
                path="/chiqimlar"
                element={
                  <PermissionProtectedRoute pagePath="/chiqimlar">
                    <Chiqimlar />
                  </PermissionProtectedRoute>
                }
              />
              <Route
                path="/suppliers"
                element={
                  <PermissionProtectedRoute pagePath="/suppliers">
                    <Taminlovchilar />
                  </PermissionProtectedRoute>
                }
              />
              <Route
                path="/qarzdorliklar"
                element={
                  <PermissionProtectedRoute pagePath="/qarzdorliklar">
                    <Mydebt />
                  </PermissionProtectedRoute>
                }
              />
              <Route
                path="/kategoriyalar"
                element={
                  <PermissionProtectedRoute pagePath="/kategoriyalar">
                    <Kategoriyalar />
                  </PermissionProtectedRoute>
                }
              />
              <Route
                path="/birliklar"
                element={
                  <PermissionProtectedRoute pagePath="/birliklar">
                    <Birliklar />
                  </PermissionProtectedRoute>
                }
              />
              <Route
                path="/staffs"
                element={
                  <PermissionProtectedRoute pagePath="/staffs">
                    <Xodimlar />
                  </PermissionProtectedRoute>
                }
              />
              <Route
                path="/mijozlar"
                element={
                  <PermissionProtectedRoute pagePath="/mijozlar">
                    <Mijozlar />
                  </PermissionProtectedRoute>
                }
              />
              <Route
                path="/hisobotlar"
                element={
                  <PermissionProtectedRoute pagePath="/hisobotlar">
                    <Hisobotlar />
                  </PermissionProtectedRoute>
                }
              />
              <Route
                path="/omborxona"
                element={
                  <PermissionProtectedRoute pagePath="/omborxona">
                    <StoreHouse />
                  </PermissionProtectedRoute>
                }
              />
              <Route
                path="/store"
                element={
                  <PermissionProtectedRoute pagePath="/store">
                    <Store />
                  </PermissionProtectedRoute>
                }
              />
              <Route
                path="/customer3"
                element={
                  <PermissionProtectedRoute pagePath="/customer3">
                    <Customer3 />
                  </PermissionProtectedRoute>
                }
              />
              <Route
                path="/credits"
                element={
                  <PermissionProtectedRoute pagePath="/credits">
                    <Kreditlar />
                  </PermissionProtectedRoute>
                }
              />
              <Route path="/permissions" element={
                <PermissionProtectedRoute pagePath="/permissions">
                  <Ruxsatlar />
                </PermissionProtectedRoute>
              } />
              <Route path="/movestore" element={
                <PermissionProtectedRoute pagePath="/movestore">
                  <MoveStore />
                </PermissionProtectedRoute>
              } />
              <Route path="/refunds" element={
                <PermissionProtectedRoute pagePath="/refunds">
                  <Refunds />
                </PermissionProtectedRoute>
              } />
              <Route path="/inventory" element={
                <PermissionProtectedRoute pagePath="/inventory">
                  <Inventory />
                </PermissionProtectedRoute>
              } />
              <Route path="/booking" element={
                <PermissionProtectedRoute pagePath="/booking">
                  <Booking />
                </PermissionProtectedRoute>
              } />
            </Routes>
          </div>
        </div>
      </div>
    </SidebarContext.Provider>
  );
}

function AppContent() {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return <div className="d-flex justify-content-center align-items-center min-vh-100">
      <div className="spinner-border" role="status">
        <span className="visually-hidden">Loading...</span>
      </div>
    </div>;
  }

  return (
    <Routes>
      <Route path="/login" element={
        isAuthenticated() ? <Navigate to="/" replace /> : <Login />
      } />
      <Route path="/register" element={
        isAuthenticated() ? <Navigate to="/" replace /> : <Register />
      } />
      <Route path="/admin-login" element={<AdminLogin />} />
      <Route path="/admin-dashboard" element={<AdminRoute><AdminDashboard /></AdminRoute>} />
      <Route path="/*" element={
        isAuthenticated() ? <AppRoutes /> : <Navigate to="/login" replace />
      } />
    </Routes>
  );
}

// Error Boundary Component
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('Error caught by boundary:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="d-flex justify-content-center align-items-center min-vh-100">
          <div className="text-center">
            <h2>Something went wrong</h2>
            <p>Please refresh the page or try logging in again.</p>
            <button
              className="btn btn-primary"
              onClick={() => {
                localStorage.removeItem('token');
                window.location.href = '/login';
              }}
            >
              Go to Login
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <PermissionProvider>
          <ExchangeRateProvider>
            <Router>
              <AppContent />
            </Router>
            <ToastContainer position="top-right" autoClose={3000} hideProgressBar={false} newestOnTop closeOnClick rtl={false} pauseOnFocusLoss draggable pauseOnHover />
          </ExchangeRateProvider>
        </PermissionProvider>
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
